"use client";

import Image from "next/image";
import { Sparkles } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";

import { useCcPrefersReducedMotion } from "@/lib/use-cc-prefers-reduced-motion";

import type { AiAgentsSubpageCopy } from "./ai-agents-page-copy";
import { AGENT_ICONS } from "./agent-icons";
import { CcAgentComposer } from "./CcAgentComposer";

type CcAgentHeroAppProps = {
  app: AiAgentsSubpageCopy["hero"]["app"];
  agents: AiAgentsSubpageCopy["hero"]["agents"];
  composer: AiAgentsSubpageCopy["composer"];
  sendLabel: string;
};

/**
 * The glowing app window under the hero (twin.so): the team of agents in a
 * sidebar, the prompt and run log in the middle. It tilts back and flattens
 * as it scrolls into view; the agent working on the current example lights
 * up in the sidebar. The scroll handler only writes one CSS variable.
 */
export function CcAgentHeroApp({ app, agents, composer, sendLabel }: CcAgentHeroAppProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const reduced = useCcPrefersReducedMotion();
  const [activeAgent, setActiveAgent] = useState(agents[0]?.id ?? "");
  const onAgentChange = useCallback((agentId: string) => setActiveAgent(agentId), []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || reduced) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      const rect = root.getBoundingClientRect();
      // 0 while the window sits low in the viewport, 1 once its top reaches ~25%.
      const progress = (window.innerHeight * 0.95 - rect.top) / (window.innerHeight * 0.7);
      root.style.setProperty("--cc-ag-p", Math.min(1, Math.max(0, progress)).toFixed(3));
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(frame);
    };
  }, [reduced]);

  return (
    <div ref={rootRef} className="cc-agent-app" data-reduced={reduced ? "true" : undefined}>
      <div className="cc-agent-app__window">
        <div className="cc-agent-app__bar" aria-hidden="true">
          <i />
          <i />
          <i />
          <span>
            <Sparkles size={11} strokeWidth={2.4} />
            {app.workspace}
          </span>
        </div>
        <div className="cc-agent-app__body">
          <aside className="cc-agent-app__sidebar" aria-hidden="true">
            <span className="cc-agent-app__sidebar-title">{app.sidebarTitle}</span>
            <ul>
              {agents.map((agent, index) => {
                const Icon = AGENT_ICONS[agent.id] ?? Sparkles;
                const working = agent.id === activeAgent;
                return (
                  <li
                    key={agent.id}
                    data-working={working ? "true" : undefined}
                    style={{ "--cc-ag-i": index } as CSSProperties}
                  >
                    <span className="cc-agent-face" data-agent={agent.id}>
                      <Icon size={14} strokeWidth={2.2} />
                    </span>
                    <span className="cc-agent-app__agent">
                      <b>{agent.name}</b>
                      <small>
                        <i />
                        {working ? app.working : app.idle}
                      </small>
                    </span>
                    <Image src={agent.logo} alt="" width={14} height={14} unoptimized />
                  </li>
                );
              })}
            </ul>
          </aside>
          <div className="cc-agent-app__main">
            <CcAgentComposer copy={composer} sendLabel={sendLabel} onAgentChange={onAgentChange} />
          </div>
        </div>
      </div>
    </div>
  );
}
